import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  styled,
  Switch
} from '@mui/material';
import { DateCalendar } from '../DatePicker';

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 6,
  '& .MuiSwitch-switchBase': {
    transitionDuration: '500ms'
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 20,
    height: 20,
    margin: 0
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#ccd7dd' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 1000
    })
  }
}));

const SuspensionForm = ({
  reasons = [],
  reasonLoading = false,
  onSuspensionDataChange,
  underReviewReason
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [suspensionDate, setSuspensionDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [checkMark, setCheckMark] = useState(!!underReviewReason || false);
  const [murReason, setMurReason] = useState('');

  useEffect(() => {
    setMurReason(underReviewReason);
  }, [underReviewReason]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(reasons.map((item) => item.category))];
    return uniqueCategories.sort();
  }, [reasons]);

  const availableGroups = useMemo(() => {
    if (!selectedCategory) return [];
    const filteredReasons = reasons.filter(
      (item) => item.category === selectedCategory
    );
    const uniqueGroups = [
      ...new Set(filteredReasons.map((item) => item.group))
    ];
    return uniqueGroups.sort();
  }, [reasons, selectedCategory]);

  const availableReasons = useMemo(() => {
    if (!selectedCategory || !selectedGroup) return [];
    return reasons
      .filter(
        (item) =>
          item.category === selectedCategory && item.group === selectedGroup
      )
      .sort((a, b) => a.order - b.order);
  }, [reasons, selectedCategory, selectedGroup]);

  const availableSeverities = useMemo(() => {
    if (!selectedReason) return [];
    const reason = reasons.find((item) => item._id === selectedReason);
    return reason ? reason.severity : [];
  }, [reasons, selectedReason]);

  useEffect(() => {
    setSelectedGroup('');
    setSelectedReason('');
    setSelectedSeverity('');
  }, [selectedCategory]);

  useEffect(() => {
    setSelectedReason('');
    setSelectedSeverity('');
  }, [selectedGroup]);

  useEffect(() => {
    setSelectedSeverity('');
  }, [selectedReason]);

  useEffect(() => {
    if (availableSeverities.length === 1 && !selectedSeverity) {
      setSelectedSeverity(availableSeverities[0].impact);
    }
  }, [availableSeverities, selectedSeverity]);

  useEffect(() => {
    if (selectedSeverity && availableSeverities.length > 0) {
      const severityData = availableSeverities.find(
        (item) => item.impact === selectedSeverity
      );
      if (severityData && severityData.suspensionDays > 0) {
        const calculatedDate = new Date();
        calculatedDate.setDate(
          calculatedDate.getDate() + severityData.suspensionDays
        );
        setSuspensionDate(calculatedDate);
      }
    }
  }, [selectedSeverity, availableSeverities]);

  const handleCalendarDate = useCallback(() => {
    setShowDatePicker((prev) => !prev);
  }, []);

  const handleDateChange = useCallback((date) => {
    setSuspensionDate(date);
  }, []);

  const submitDate = useCallback(() => {
    setShowDatePicker(false);
  }, []);

  const cancelDate = useCallback(() => {
    setShowDatePicker(false);
  }, []);

  const resetDate = useCallback(() => {
    setSuspensionDate(new Date());
    setShowDatePicker(false);
  }, []);

  useEffect(() => {
    if (
      onSuspensionDataChange &&
      (selectedCategory || selectedGroup || selectedReason || selectedSeverity)
    ) {
      const selectedReasonData = reasons.find(
        (item) => item._id === selectedReason
      );
      const selectedSeverityData = selectedReason
        ? reasons
            .find((item) => item._id === selectedReason)
            ?.severity?.find((item) => item.impact === selectedSeverity)
        : null;

      onSuspensionDataChange({
        category: selectedCategory,
        group: selectedGroup,
        reasonId: selectedReason,
        reasonText: selectedReasonData?.reason || '',
        severity: selectedSeverity,
        suspensionDays: selectedSeverityData?.suspensionDays || 0,
        suspensionDate: suspensionDate,
        ...(checkMark && { underReviewReason: murReason })
      });
    }
  }, [
    selectedCategory,
    selectedGroup,
    selectedReason,
    selectedSeverity,
    suspensionDate,
    murReason
  ]);

  const selectStyle = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#e3eaf3'
        },
        '&:hover fieldset': {
          borderColor: '#e3eaf3'
        },
        '&.Mui-focused fieldset': {
          borderColor: '#007bff'
        },
        '&.Mui-disabled': {
          backgroundColor: '#f5f5f5',
          '& fieldset': {
            borderColor: '#e0e0e0'
          }
        }
      },
      '& .MuiInputLabel-root.Mui-disabled': {
        color: '#a0a0a0'
      },
      '& .MuiSelect-select.Mui-disabled': {
        color: '#a0a0a0',
        WebkitTextFillColor: '#a0a0a0'
      },
      '& .MuiOutlinedInput-input': {
        padding: '12px'
      },
      '& .MuiInputLabel-root': {
        transform: 'translate(14px, 12px) scale(1)'
      },
      '& .MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)'
      }
    }),
    []
  );

  return (
    <div style={{ width: '700px', marginTop: '-1.75rem' }}>
      {/* Category Dropdown */}
      <FormControl fullWidth margin="normal" sx={selectStyle}>
        <InputLabel id="category-label">Category</InputLabel>
        <Select
          labelId="category-label"
          value={selectedCategory}
          label="Category"
          onChange={(e) => setSelectedCategory(e.target.value)}
          disabled={reasonLoading || categories.length === 0}
        >
          {categories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {/* Category Reason (Group) Dropdown */}
      <FormControl fullWidth margin="normal" sx={selectStyle}>
        <InputLabel id="group-label">Category reason</InputLabel>
        <Select
          labelId="group-label"
          value={selectedGroup}
          label="Category reason"
          onChange={(e) => setSelectedGroup(e.target.value)}
          disabled={
            reasonLoading || !selectedCategory || availableGroups.length === 0
          }
        >
          {availableGroups.map((group) => (
            <MenuItem key={group} value={group}>
              {group}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {/* Sub-Category Reason Dropdown */}
      <FormControl fullWidth margin="normal" sx={selectStyle}>
        <InputLabel id="reason-label">Sub-Category reason</InputLabel>
        <Select
          labelId="reason-label"
          value={selectedReason}
          label="Sub-Category reason"
          onChange={(e) => setSelectedReason(e.target.value)}
          disabled={
            reasonLoading || !selectedGroup || availableReasons.length === 0
          }
          inputProps={{
            sx: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '400px'
            }
          }}
        >
          {availableReasons.map((reason) => (
            <MenuItem key={reason._id} value={reason._id}>
              {reason.reason}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {/* Severity Dropdown or Display */}
      {availableSeverities.length === 1 ? (
        // Display severity when only one option is available
        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: '16px'
            }}
          >
            Severity:
          </label>
          <div
            style={{
              padding: '8px 14px',
              borderRadius: '4px',
              border: '1px solid #e3eaf3',
              backgroundColor: '#f8f9fa',
              fontSize: '16px',
              color: 'rgba(0, 0, 0, 0.87)'
            }}
          >
            {availableSeverities[0].impact} (
            {availableSeverities[0].suspensionDays} days)
          </div>
        </div>
      ) : (
        // Show dropdown when multiple options are available
        <FormControl fullWidth margin="normal" sx={selectStyle}>
          <InputLabel id="severity-label">Severity</InputLabel>
          <Select
            labelId="severity-label"
            value={selectedSeverity}
            label="Severity"
            onChange={(e) => setSelectedSeverity(e.target.value)}
            disabled={
              reasonLoading ||
              !selectedReason ||
              availableSeverities.length === 0
            }
          >
            {availableSeverities.map((severity, index) => (
              <MenuItem key={index} value={severity.impact}>
                {severity.impact} ({severity.suspensionDays} days)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Suspension Date */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '8px 0 4px 0',
          padding: '0 14px'
        }}
      >
        <label
          style={{
            margin: 0,
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: '16px'
          }}
        >
          Suspension date:
        </label>
        <div style={{ width: '250px', position: 'relative' }}>
          <DateCalendar
            handleCalendarDate={handleCalendarDate}
            showDatePicker={showDatePicker}
            onChange={handleDateChange}
            submitDate={submitDate}
            cancelDate={cancelDate}
            resetDate={resetDate}
            date={suspensionDate}
            showDate={suspensionDate}
            minDate={new Date()}
            placeholder="MM-dd-yyyy"
            sx={{
              width: '100%',
              position: 'relative'
            }}
          />
        </div>
      </div>

      {/* Under Review Reason */}
      <div style={{ marginTop: '8px' }}>
        <label
          style={{
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: '16px',
            margin: 0,
            flexShrink: 0
          }}
        >
          Mark under review
        </label>
        <FormControlLabel
          control={
            <Android12Switch
              checked={checkMark}
              onChange={(e) => setCheckMark(e.target.checked)}
            />
          }
          label=""
          style={{ margin: 0 }}
        />
        {checkMark && (
          <textarea
            type="text"
            value={murReason}
            onChange={(e) => setMurReason(e.target.value)}
            style={{
              fontSize: '15px',
              resize: 'none',
              overflow: 'auto',
              width: '100%',
              borderRadius: '10px',
              padding: '6px'
            }}
            rows="2"
          />
        )}
      </div>
    </div>
  );
};

export default SuspensionForm;
